import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Plus, TrendingDown, FileText, Save, Trash2, User, ArrowLeft, CalendarPlus, Search, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import { useSearchParams } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

export const EvolutionManagement = () => {
  const [searchParams] = useSearchParams();
  const preselected = searchParams.get('patient') || '';
  const [patients, setPatients] = useState([]);
  const [records, setRecords] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(preselected);
  const [pronForm, setPronForm] = useState({});
  const [savingPron, setSavingPron] = useState(false);
  const [activeTab, setActiveTab] = useState('prontuario');
  const [searchTerm, setSearchTerm] = useState('');
  const [retornoForm, setRetornoForm] = useState({ queixa: '', conduta: '', data: new Date().toISOString().split('T')[0] });
  const [showRetornoForm, setShowRetornoForm] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [form, setForm] = useState({ patient_id: '', weight: '', height: '', notes: '', mounjaro_dose: '', date: new Date().toISOString().split('T')[0] });

  const fetchData = useCallback(async () => {
    try {
      const [patsRes, recsRes] = await Promise.all([
        axios.get(`${API_URL}/api/patients`, { withCredentials: true }),
        axios.get(`${API_URL}/api/evolution`, { withCredentials: true })
      ]);
      setPatients(patsRes.data);
      setRecords(recsRes.data);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const fetchProntuario = useCallback(async (pid) => {
    try {
      const { data } = await axios.get(`${API_URL}/api/prontuario/${pid}`, { withCredentials: true });
      const normalized = data ? { ...data } : {};
      // Legacy data: `exames` may have been stored as an array. Coerce to string.
      if (Array.isArray(normalized.exames)) {
        normalized.exames = normalized.exames.filter(Boolean).join('\n');
      }
      setPronForm(normalized);
    } catch (e) { setPronForm({}); }
  }, []);

  useEffect(() => {
    if (selectedPatient) { fetchProntuario(selectedPatient); setActiveTab('prontuario'); }
    else setPronForm({});
  }, [selectedPatient, fetchProntuario]);

  const getPatient = (id) => patients.find(p => p.patient_id === id);
  const getName = (id) => getPatient(id)?.name || id;
  const getType = (id) => getPatient(id)?.consultation_type || 'clinica_geral';
  const isTirz = selectedPatient && getType(selectedPatient) === 'tirzepatida';

  const saveProntuario = async () => {
    setSavingPron(true);
    try {
      const payload = { ...pronForm, patient_id: selectedPatient, tipo: getType(selectedPatient) };
      ['peso_inicial','altura','peso_ideal','peso_combinado','perda_total_esperada','meta_combinada'].forEach(k => { if (payload[k]) payload[k] = parseFloat(payload[k]); });
      if (!payload.data_inicio) payload.data_inicio = new Date().toISOString().split('T')[0];
      await axios.post(`${API_URL}/api/prontuario`, payload, { withCredentials: true });
      // Also create evolution record for first consultation
      if (!pronForm.created_at && payload.queixa_principal) {
        await axios.post(`${API_URL}/api/evolution`, {
          patient_id: selectedPatient, source: 'doctor',
          queixa_principal: payload.queixa_principal, conduta_final: payload.conduta_final,
          date: payload.data_inicio, is_return: false
        }, { withCredentials: true }).catch(() => {});
      }
      toast.success('Prontuario salvo!');
      fetchProntuario(selectedPatient);
      fetchData();
    } catch (e) { toast.error('Erro ao salvar'); }
    setSavingPron(false);
  };

  const addRetorno = async () => {
    try {
      await axios.post(`${API_URL}/api/prontuario/${selectedPatient}/retorno`, retornoForm, { withCredentials: true });
      toast.success('Retorno adicionado!');
      setRetornoForm({ queixa: '', conduta: '', data: new Date().toISOString().split('T')[0] });
      setShowRetornoForm(false);
      fetchProntuario(selectedPatient);
      fetchData();
    } catch (e) { toast.error('Erro ao adicionar retorno'); }
  };

  const handleCreateEvolution = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/evolution`, { ...form, weight: parseFloat(form.weight), height: form.height ? parseFloat(form.height) : null }, { withCredentials: true });
      toast.success('Registro adicionado!');
      setIsCreateOpen(false);
      setForm({ patient_id: '', weight: '', height: '', notes: '', mounjaro_dose: '', date: new Date().toISOString().split('T')[0] });
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Erro'); }
  };

  const filtered = selectedPatient ? records.filter(r => r.patient_id === selectedPatient) : records;
  const sortedWeightRecs = [...filtered].filter(r => r.weight).sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  const chartData = sortedWeightRecs.map(r => ({ date: new Date(r.created_at).toLocaleDateString('pt-BR'), peso: r.weight, imc: r.bmi || null }));
  // Consolidated chart data — includes weight + all measurements over time
  const consolidatedChartData = sortedWeightRecs.map(r => {
    const row = { date: new Date(r.created_at).toLocaleDateString('pt-BR'), peso: r.weight };
    ['abdomen','cintura','quadril','braco_d','braco_e','coxa_d','coxa_e','panturrilha_d','panturrilha_e'].forEach(k => {
      if (r[k] != null) row[k] = r[k];
    });
    return row;
  });
  const MEAS_META = [
    { key: 'abdomen', label: 'Abdômen', color: '#e11d48' },
    { key: 'cintura', label: 'Cintura', color: '#3b82f6' },
    { key: 'quadril', label: 'Quadril', color: '#8b5cf6' },
    { key: 'braco_d', label: 'Braço D', color: '#f59e0b' },
    { key: 'braco_e', label: 'Braço E', color: '#d97706' },
    { key: 'coxa_d', label: 'Coxa D', color: '#f97316' },
    { key: 'coxa_e', label: 'Coxa E', color: '#c2410c' },
    { key: 'panturrilha_d', label: 'Panturrilha D', color: '#10b981' },
    { key: 'panturrilha_e', label: 'Panturrilha E', color: '#047857' },
  ];
  const activeMeasKeys = MEAS_META.filter(m => consolidatedChartData.some(r => r[m.key] != null));
  const filteredPatients = patients.filter(p => !searchTerm || p.name?.toLowerCase().includes(searchTerm.toLowerCase()));

  // Patient not selected - show patient cards
  if (!selectedPatient) {
    return (
      <div className="space-y-5" data-testid="evolution-management">
        <div>
          <h2 className="text-3xl font-medium text-[#0F1B3D]" style={{ fontFamily: 'Outfit, sans-serif' }}>Evolucao</h2>
          <p className="text-[#5F6B85]">Selecione um paciente para acompanhar</p>
        </div>

        <div className="flex gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#7A85A0]" />
            <Input placeholder="Buscar paciente..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10" data-testid="search-patient" />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredPatients.map(p => {
            const patRecords = records.filter(r => r.patient_id === p.patient_id);
            const lastWeight = patRecords.find(r => r.weight);
            const isG = p.consultation_type !== 'tirzepatida';
            return (
              <Card key={p.patient_id} className="border-[#E5DCC8] hover:border-[#0F1B3D] transition-colors cursor-pointer" onClick={() => setSelectedPatient(p.patient_id)} data-testid={`patient-card-${p.patient_id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-full ${isG ? 'bg-[#0F1B3D]/10' : 'bg-[#B8845C]/10'}`}><User className={`h-5 w-5 ${isG ? 'text-[#0F1B3D]' : 'text-[#B8845C]'}`} /></div>
                      <div>
                        <h3 className="font-semibold text-[#0F1B3D]">{p.name}</h3>
                        <span className={`text-xs font-medium ${isG ? 'text-[#0F1B3D]' : 'text-[#B8845C]'}`}>{isG ? 'Clinica Geral' : 'Tirzepatida'}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-4 text-sm text-[#5F6B85]">
                    <span>{patRecords.length} registros</span>
                    {lastWeight && <span>Ultimo: {lastWeight.weight}kg</span>}
                  </div>
                </CardContent>
              </Card>
            );
          })}
          {filteredPatients.length === 0 && <p className="text-[#7A85A0] col-span-3 text-center py-8">Nenhum paciente encontrado</p>}
        </div>
      </div>
    );
  }

  const pat = getPatient(selectedPatient);
  const retornos = pronForm.retornos || [];

  // Patient selected - show prontuario + evolution
  return (
    <div className="space-y-5" data-testid="evolution-management">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => setSelectedPatient('')} className="text-[#5F6B85]"><ArrowLeft className="h-4 w-4 mr-1" />Voltar</Button>
        <div>
          <h2 className="text-2xl font-medium text-[#0F1B3D]" style={{ fontFamily: 'Outfit, sans-serif' }}>{getName(selectedPatient)}</h2>
          <span className={`text-xs font-medium ${isTirz ? 'text-[#B8845C]' : 'text-[#0F1B3D]'}`}>{isTirz ? 'Tirzepatida' : 'Clinica Geral'}</span>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="prontuario">Prontuario</TabsTrigger>
          <TabsTrigger value="timeline">Timeline</TabsTrigger>
          {isTirz && <TabsTrigger value="evolucao">Peso / IMC</TabsTrigger>}
        </TabsList>

        {/* PRONTUARIO TAB */}
        <TabsContent value="prontuario" className="space-y-5">
          {isTirz
            ? <ProntuarioTirzepatida pronForm={pronForm} setPronForm={setPronForm} saveProntuario={saveProntuario} savingPron={savingPron} getName={getName} selectedPatient={selectedPatient} />
            : <ProntuarioClinicaGeral pronForm={pronForm} setPronForm={setPronForm} saveProntuario={saveProntuario} savingPron={savingPron} getName={getName} selectedPatient={selectedPatient} />
          }
        </TabsContent>

        {/* TIMELINE TAB */}
        <TabsContent value="timeline" className="space-y-5">
          <Card className="border-[#E5DCC8]">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Linha do Tempo - {getName(selectedPatient)}</CardTitle>
                <Button size="sm" onClick={() => setShowRetornoForm(!showRetornoForm)} className="bg-[#A8174E] hover:bg-[#8E1142] text-white"><Plus className="h-4 w-4 mr-1" />Registrar Retorno</Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* New return form */}
              {showRetornoForm && (
                <div className="mb-6 p-4 bg-[#FAF6EE] rounded-lg space-y-3">
                  <h4 className="font-semibold text-[#0F1B3D] text-sm">Novo Retorno</h4>
                  <div className="space-y-2"><Label className="text-xs">Data</Label><Input type="date" value={retornoForm.data} onChange={e => setRetornoForm({...retornoForm, data: e.target.value})} className="h-8" /></div>
                  <div className="space-y-2"><Label className="text-xs">Queixa Principal</Label><textarea className="w-full border border-[#E5DCC8] rounded-md p-2 text-sm min-h-[60px] focus:outline-none focus:ring-1 focus:ring-[#0F1B3D]" value={retornoForm.queixa} onChange={e => setRetornoForm({...retornoForm, queixa: e.target.value})} placeholder="Queixa do paciente..." /></div>
                  <div className="space-y-2"><Label className="text-xs">Conduta Final</Label><textarea className="w-full border border-[#E5DCC8] rounded-md p-2 text-sm min-h-[60px] focus:outline-none focus:ring-1 focus:ring-[#0F1B3D]" value={retornoForm.conduta} onChange={e => setRetornoForm({...retornoForm, conduta: e.target.value})} placeholder="Conduta adotada..." /></div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={addRetorno} className="bg-[#A8174E] hover:bg-[#8E1142] text-white"><Save className="h-3.5 w-3.5 mr-1" />Salvar</Button>
                    <Button size="sm" variant="outline" onClick={() => setShowRetornoForm(false)}>Cancelar</Button>
                  </div>
                </div>
              )}

              {/* First consultation */}
              <div className="relative pl-6 space-y-4">
                <div className="absolute left-2 top-0 bottom-0 w-px bg-[#E5DCC8]" />

                {pronForm.queixa_principal && (
                  <div className="relative">
                    <div className="absolute -left-[18px] top-1 w-3 h-3 rounded-full bg-[#0F1B3D] border-2 border-white" />
                    <div className="bg-white rounded-lg p-4 border border-[#0F1B3D]/20">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs font-semibold text-white bg-[#0F1B3D] px-2 py-0.5 rounded">1a Consulta</span>
                        <span className="text-xs text-[#7A85A0]">{pronForm.data_inicio ? new Date(pronForm.data_inicio + 'T12:00').toLocaleDateString('pt-BR') : ''}</span>
                      </div>
                      <p className="text-sm text-[#5F6B85] mb-1"><strong className="text-[#0F1B3D]">Queixa:</strong> {pronForm.queixa_principal}</p>
                      {pronForm.conduta_final && <p className="text-sm text-[#5F6B85]"><strong className="text-[#0F1B3D]">Conduta:</strong> {pronForm.conduta_final}</p>}
                    </div>
                  </div>
                )}

                {/* Returns */}
                {retornos.map((ret, i) => (
                  <div key={`ret-${i}`} className="relative">
                    <div className="absolute -left-[18px] top-1 w-3 h-3 rounded-full bg-[#B8845C] border-2 border-white" />
                    <div className="bg-white rounded-lg p-4 border border-[#E5DCC8]">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs font-semibold text-white bg-[#B8845C] px-2 py-0.5 rounded">Retorno {i + 1}</span>
                        <span className="text-xs text-[#7A85A0]">{ret.data ? new Date(ret.data + 'T12:00').toLocaleDateString('pt-BR') : ''}</span>
                      </div>
                      {ret.queixa && <p className="text-sm text-[#5F6B85] mb-1"><strong className="text-[#0F1B3D]">Queixa:</strong> {ret.queixa}</p>}
                      {ret.conduta && <p className="text-sm text-[#5F6B85]"><strong className="text-[#0F1B3D]">Conduta:</strong> {ret.conduta}</p>}
                    </div>
                  </div>
                ))}

                {!pronForm.queixa_principal && retornos.length === 0 && (
                  <div className="text-center py-8 text-[#7A85A0]">
                    <FileText className="h-10 w-10 mx-auto mb-2" />
                    <p>Nenhum registro ainda. Preencha o prontuario na aba "Prontuario".</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* PESO/IMC TAB (only tirzepatida) */}
        {isTirz && (
          <TabsContent value="evolucao" className="space-y-5">
            {/* CONSOLIDATED CHART — weight + all circumferences with dual Y axis */}
            {consolidatedChartData.length >= 2 && activeMeasKeys.length > 0 && (
              <Card className="border-[#E5DCC8] border-l-4 border-l-[#A8174E]" data-testid="doctor-consolidated-chart">
                <CardHeader>
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <CardTitle className="flex items-center gap-2"><TrendingDown className="h-5 w-5 text-[#A8174E]" />Visão Consolidada — Peso + Circunferências</CardTitle>
                      <p className="text-xs text-[#7A85A0] mt-1">Peso em kg (eixo esquerdo) e circunferências em cm (eixo direito) sobrepostos ao longo do tempo.</p>
                    </div>
                    <div className="text-xs text-[#5F6B85] hidden sm:block">
                      {consolidatedChartData.length} registro(s)
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={360}>
                    <LineChart data={consolidatedChartData} margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5DCC8" />
                      <XAxis dataKey="date" tick={{ fill: '#5F6B85', fontSize: 11 }} />
                      <YAxis yAxisId="left" tick={{ fill: '#0F1B3D', fontSize: 11 }} label={{ value: 'Peso (kg)', angle: -90, position: 'insideLeft', style: { fill: '#0F1B3D', fontSize: 11 } }} />
                      <YAxis yAxisId="right" orientation="right" tick={{ fill: '#5F6B85', fontSize: 11 }} label={{ value: 'Circ. (cm)', angle: 90, position: 'insideRight', style: { fill: '#5F6B85', fontSize: 11 } }} />
                      <Tooltip contentStyle={{ background: '#FAF6EE', border: '1px solid #E5DCC8', borderRadius: 8 }} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Line yAxisId="left" type="monotone" dataKey="peso" stroke="#0F1B3D" strokeWidth={3} name="Peso (kg)" dot={{ fill: '#0F1B3D', r: 4 }} activeDot={{ r: 6 }} />
                      {activeMeasKeys.map(m => (
                        <Line
                          key={m.key}
                          yAxisId="right"
                          type="monotone"
                          dataKey={m.key}
                          stroke={m.color}
                          strokeWidth={1.75}
                          name={m.label}
                          dot={{ r: 2 }}
                          connectNulls
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                  {/* Variation summary — last vs first */}
                  <div className="mt-4 pt-4 border-t border-[#FAF6EE]">
                    <div className="text-xs text-[#7A85A0] uppercase tracking-wide mb-2 font-medium">Variação (último vs primeiro)</div>
                    <div className="flex flex-wrap gap-2">
                      {(() => {
                        const items = [];
                        const first = sortedWeightRecs[0];
                        const last = sortedWeightRecs[sortedWeightRecs.length - 1];
                        if (first && last) {
                          const delta = +(last.weight - first.weight).toFixed(1);
                          items.push(
                            <span key="peso" className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#FAF6EE] border border-[#E5DCC8] text-xs">
                              <span className="inline-block w-2 h-2 rounded-full" style={{ background: '#0F1B3D' }} />
                              <span className="text-[#5F6B85]">Peso:</span>
                              <span className={`font-semibold ${delta < 0 ? 'text-[#10b981]' : delta > 0 ? 'text-[#A8174E]' : 'text-[#0F1B3D]'}`}>
                                {delta > 0 ? '+' : ''}{delta} kg
                              </span>
                            </span>
                          );
                        }
                        activeMeasKeys.forEach(m => {
                          const firstVal = sortedWeightRecs.find(r => r[m.key] != null)?.[m.key];
                          const lastVal = [...sortedWeightRecs].reverse().find(r => r[m.key] != null)?.[m.key];
                          if (firstVal != null && lastVal != null) {
                            const delta = +(lastVal - firstVal).toFixed(1);
                            items.push(
                              <span key={m.key} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#FAF6EE] border border-[#E5DCC8] text-xs">
                                <span className="inline-block w-2 h-2 rounded-full" style={{ background: m.color }} />
                                <span className="text-[#5F6B85]">{m.label}:</span>
                                <span className={`font-semibold ${delta < 0 ? 'text-[#10b981]' : delta > 0 ? 'text-[#A8174E]' : 'text-[#0F1B3D]'}`}>
                                  {delta > 0 ? '+' : ''}{delta} cm
                                </span>
                              </span>
                            );
                          }
                        });
                        return items;
                      })()}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {chartData.length >= 2 && (
              <Card className="border-[#E5DCC8]">
                <CardHeader><CardTitle className="flex items-center gap-2"><TrendingDown className="h-5 w-5 text-[#0F1B3D]" />Evolucao de Peso</CardTitle></CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5DCC8" />
                      <XAxis dataKey="date" tick={{ fill: '#5F6B85', fontSize: 12 }} />
                      <YAxis tick={{ fill: '#5F6B85', fontSize: 12 }} />
                      <Tooltip /><Legend />
                      <Line type="monotone" dataKey="peso" stroke="#0F1B3D" strokeWidth={2} name="Peso (kg)" dot={{ fill: '#0F1B3D' }} />
                      <Line type="monotone" dataKey="imc" stroke="#B8845C" strokeWidth={2} name="IMC" dot={{ fill: '#B8845C' }} />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}
            <div className="space-y-3">
              {filtered.filter(r => r.weight).length === 0 ? (
                <Card className="border-[#E5DCC8]"><CardContent className="p-8 text-center"><p className="text-[#7A85A0]">Nenhum registro de peso</p></CardContent></Card>
              ) : filtered.filter(r => r.weight).map(rec => {
                const MEAS_LIST = [
                  ['abdomen','Abd', '#e11d48'],['cintura','Cint', '#3b82f6'],['quadril','Quad', '#8b5cf6'],
                  ['braco_d','Braço D', '#f59e0b'],['braco_e','Braço E', '#f59e0b'],
                  ['coxa_d','Coxa D', '#f97316'],['coxa_e','Coxa E', '#f97316'],
                  ['panturrilha_d','Pant D', '#10b981'],['panturrilha_e','Pant E', '#10b981'],
                ];
                const hasMeas = MEAS_LIST.some(([k]) => rec[k] != null);
                return (
                  <Card key={rec.record_id} className="border-[#E5DCC8]">
                    <CardContent className="p-4 space-y-3">
                      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 text-sm">
                        <div><span className="text-[#7A85A0]">Data</span><p className="font-medium">{new Date(rec.created_at).toLocaleDateString('pt-BR')}</p></div>
                        <div><span className="text-[#7A85A0]">Peso</span><p className="text-lg font-semibold text-[#0F1B3D]">{rec.weight} kg</p></div>
                        {rec.bmi && <div><span className="text-[#7A85A0]">IMC</span><p className="text-lg font-semibold text-[#B8845C]">{rec.bmi}</p></div>}
                        {rec.mounjaro_dose && <div><span className="text-[#7A85A0]">Dose</span><p className="font-semibold text-[#0F1B3D]">{rec.mounjaro_dose}</p></div>}
                        <div><span className="text-[#7A85A0]">Fonte</span><p className="font-medium">{rec.source === 'patient' ? 'Paciente' : 'Doutora'}</p></div>
                      </div>
                      {hasMeas && (
                        <div className="pt-2 border-t border-[#FAF6EE]">
                          <div className="text-xs text-[#7A85A0] mb-2 font-medium uppercase tracking-wide">Circunferências (cm)</div>
                          <div className="flex flex-wrap gap-2">
                            {MEAS_LIST.filter(([k]) => rec[k] != null).map(([k, label, color]) => (
                              <div key={k} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#FAF6EE] border border-[#E5DCC8] text-xs">
                                <span className="inline-block w-2 h-2 rounded-full" style={{ background: color }} />
                                <span className="text-[#5F6B85]">{label}:</span>
                                <span className="font-semibold text-[#0F1B3D]">{rec[k]}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

/* ============ CLINICA GERAL PRONTUARIO ============ */
const ProntuarioClinicaGeral = ({ pronForm, setPronForm, saveProntuario, savingPron, getName, selectedPatient }) => {
  if (!pronForm.data_inicio) setPronForm(prev => ({ ...prev, data_inicio: new Date().toISOString().split('T')[0] }));

  return (
    <Card className="border-[#E5DCC8]">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div><CardTitle className="text-lg">Prontuario - {getName(selectedPatient)}</CardTitle><CardDescription>Dra Fernanda Garcia Passos - CRM 239.124</CardDescription></div>
          <Button onClick={saveProntuario} disabled={savingPron} className="bg-[#A8174E] hover:bg-[#8E1142] text-white"><Save className="h-4 w-4 mr-2" />{savingPron ? 'Salvando...' : 'Salvar'}</Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2"><Label>Data</Label><Input type="date" value={pronForm.data_inicio || ''} onChange={e => setPronForm({...pronForm, data_inicio: e.target.value})} /></div>
          <div className="space-y-2"><Label>Idade</Label><Input value={pronForm.idade || ''} onChange={e => setPronForm({...pronForm, idade: e.target.value})} placeholder="Ex: 77 anos" /></div>
          <div className="space-y-2"><Label>Atividade Fisica</Label><Input value={pronForm.atividade_fisica || ''} onChange={e => setPronForm({...pronForm, atividade_fisica: e.target.value})} /></div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2"><Label>AP (Antecedentes Pessoais)</Label><textarea className="w-full border border-[#E5DCC8] rounded-md p-2 text-sm min-h-[70px] focus:outline-none focus:ring-1 focus:ring-[#0F1B3D]" value={pronForm.ap || ''} onChange={e => setPronForm({...pronForm, ap: e.target.value})} /></div>
          <div className="space-y-2"><Label>MUC (Medicacoes em Uso)</Label><textarea className="w-full border border-[#E5DCC8] rounded-md p-2 text-sm min-h-[70px] focus:outline-none focus:ring-1 focus:ring-[#0F1B3D]" value={pronForm.muc || ''} onChange={e => setPronForm({...pronForm, muc: e.target.value})} /></div>
        </div>
        <div className="space-y-2"><Label>Alergias</Label><Input value={pronForm.alergias || ''} onChange={e => setPronForm({...pronForm, alergias: e.target.value})} /></div>
        <div className="space-y-2"><Label>Queixa Principal</Label><textarea className="w-full border border-[#E5DCC8] rounded-md p-2 text-sm min-h-[70px] focus:outline-none focus:ring-1 focus:ring-[#0F1B3D]" value={pronForm.queixa_principal || ''} onChange={e => setPronForm({...pronForm, queixa_principal: e.target.value})} /></div>
        <div className="space-y-2"><Label>Conduta Final</Label><textarea className="w-full border border-[#E5DCC8] rounded-md p-2 text-sm min-h-[70px] focus:outline-none focus:ring-1 focus:ring-[#0F1B3D]" value={pronForm.conduta_final || ''} onChange={e => setPronForm({...pronForm, conduta_final: e.target.value})} /></div>

        <hr className="border-[#E5DCC8]" />
        <ExamesTextarea pronForm={pronForm} setPronForm={setPronForm} />

        <hr className="border-[#E5DCC8]" />
        <NextReturnSection pronForm={pronForm} setPronForm={setPronForm} pronData_inicio={pronForm.data_inicio} />
      </CardContent>
    </Card>
  );
};

const ExamesTextarea = ({ pronForm, setPronForm }) => (
  <div className="space-y-2">
    <h3 className="text-lg font-semibold text-[#0F1B3D]">Exames Laboratoriais</h3>
    <p className="text-xs text-[#7A85A0]">Cole aqui os resultados dos exames laboratoriais do paciente (hemograma, renal, hepatico, diabetes, lipidico, vitaminas/minerais, tireoide, urina e outros).</p>
    <textarea
      className="w-full border border-[#E5DCC8] rounded-md p-3 text-sm min-h-[260px] font-mono focus:outline-none focus:ring-1 focus:ring-[#0F1B3D]"
      value={pronForm.exames || ''}
      onChange={e => setPronForm({ ...pronForm, exames: e.target.value })}
      placeholder={"Ex:\nHEMOGRAMA (12/03/2026): HB 13.2 | HT 39 | LEU 6.500 | PLAQ 240.000\nRENAL: CREAT 0.8 | UR 32 | NA 140 | K 4.1\nHEPATICO: TGO 22 | TGP 18\n..."}
      data-testid="exames-textarea"
    />
  </div>
);

const NextReturnSection = ({ pronForm, setPronForm, pronData_inicio }) => {
  const clear = () => setPronForm({
    ...pronForm,
    next_return_date: '',
    next_return_time: '',
    next_return_notes: '',
  });

  // Suggest a date = last consultation date (data_inicio) + 3 months
  const suggestDate = () => {
    const base = pronData_inicio || pronForm.data_inicio || new Date().toISOString().slice(0, 10);
    const d = new Date(base + 'T12:00:00');
    d.setMonth(d.getMonth() + 3);
    const iso = d.toISOString().slice(0, 10);
    setPronForm({ ...pronForm, next_return_date: iso });
  };

  // Auto-fill on first render if empty
  React.useEffect(() => {
    if (!pronForm.next_return_date && (pronData_inicio || pronForm.data_inicio)) {
      const base = pronData_inicio || pronForm.data_inicio;
      const d = new Date(base + 'T12:00:00');
      if (!isNaN(d)) {
        d.setMonth(d.getMonth() + 3);
        setPronForm(prev => ({ ...prev, next_return_date: d.toISOString().slice(0, 10) }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pronData_inicio, pronForm.data_inicio]);

  const displayDate = pronForm.next_return_date
    ? new Date(pronForm.next_return_date + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' })
    : null;

  return (
    <div className="rounded-2xl border-2 border-[#A8174E]/20 bg-gradient-to-br from-[#FFF7F0] via-[#FAF6EE] to-[#FFF7F0] p-6 shadow-sm" data-testid="next-return-section">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-full bg-[#A8174E] text-white flex items-center justify-center shrink-0">
            <Calendar className="h-5 w-5" />
          </div>
          <div>
            <h3 className="text-xl font-semibold text-[#0F1B3D]">Próximo Retorno Programado</h3>
            <p className="text-sm text-[#5F6B85] mt-0.5">Sugestão automática 3 meses após a última consulta. Não cria consulta nem ocupa horário.</p>
          </div>
        </div>
        {(pronForm.next_return_date || pronForm.next_return_notes) && (
          <Button type="button" variant="outline" size="sm" onClick={clear} className="border-[#E5DCC8] shrink-0" data-testid="next-return-clear">
            Limpar
          </Button>
        )}
      </div>

      {displayDate && (
        <div className="mb-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#A8174E] text-white text-sm font-medium shadow" data-testid="next-return-display">
          <Calendar className="h-4 w-4" />
          {displayDate}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-[#0F1B3D] font-medium">Data sugerida</Label>
          <div className="flex gap-2">
            <Input type="date" value={pronForm.next_return_date || ''} onChange={e => setPronForm({ ...pronForm, next_return_date: e.target.value })} data-testid="next-return-date" className="flex-1" />
            <Button type="button" variant="outline" size="sm" onClick={suggestDate} className="border-[#A8174E] text-[#A8174E] hover:bg-[#A8174E]/5 shrink-0" data-testid="next-return-suggest">
              +3 meses
            </Button>
          </div>
        </div>
        <div className="space-y-2">
          <Label className="text-[#0F1B3D] font-medium">Observação (opcional)</Label>
          <Input value={pronForm.next_return_notes || ''} onChange={e => setPronForm({ ...pronForm, next_return_notes: e.target.value })} placeholder="Ex: trazer resultados de exames" data-testid="next-return-notes" />
        </div>
      </div>
    </div>
  );
};

/* ============ TIRZEPATIDA PRONTUARIO ============ */
const ProntuarioTirzepatida = ({ pronForm, setPronForm, saveProntuario, savingPron, getName, selectedPatient }) => {
  const imcInicial = pronForm.peso_inicial && pronForm.altura ? (pronForm.peso_inicial / ((pronForm.altura / 100) ** 2)).toFixed(1) : '';
  const defaultCronUso = [
    { fase: 'Adaptacao', semanas: '', dose: '2.5mg', perda_esperada: '', perda_total: '', peso_estimado: '' },
    { fase: 'Intensificacao', semanas: '', dose: '5mg', perda_esperada: '', perda_total: '', peso_estimado: '' },
    { fase: 'Consolidacao inicial', semanas: '', dose: '7.5mg', perda_esperada: '', perda_total: '', peso_estimado: '' },
    { fase: 'Consolidacao final', semanas: '', dose: '10mg', perda_esperada: '', perda_total: '', peso_estimado: '' },
  ];
  const defaultCronDesmame = [
    { fase: 'Manutencao alta', semanas: '', dose: '' },
    { fase: 'Reducao intermediaria', semanas: '', dose: '' },
    { fase: 'Reducao final', semanas: '', dose: '' },
    { fase: 'Suspensao', semanas: '', dose: '' },
    { fase: 'Termino', semanas: '', dose: 'Suspende' },
  ];
  const cronUso = pronForm.cronograma_uso?.length ? pronForm.cronograma_uso : defaultCronUso;
  const cronDesmame = pronForm.cronograma_desmame?.length ? pronForm.cronograma_desmame : defaultCronDesmame;
  const updateCronUso = (i, f, v) => { const u = [...cronUso]; u[i] = { ...u[i], [f]: v }; setPronForm({ ...pronForm, cronograma_uso: u }); };
  const updateCronDesmame = (i, f, v) => { const u = [...cronDesmame]; u[i] = { ...u[i], [f]: v }; setPronForm({ ...pronForm, cronograma_desmame: u }); };

  if (!pronForm.data_inicio) setPronForm(prev => ({ ...prev, data_inicio: new Date().toISOString().split('T')[0] }));

  return (
    <Card className="border-[#E5DCC8]">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div><CardTitle className="text-lg">Prontuario Tirzepatida - {getName(selectedPatient)}</CardTitle><CardDescription>Dra Fernanda Garcia Passos - CRM 239.124</CardDescription></div>
          <Button onClick={saveProntuario} disabled={savingPron} className="bg-[#A8174E] hover:bg-[#8E1142] text-white"><Save className="h-4 w-4 mr-2" />{savingPron ? 'Salvando...' : 'Salvar'}</Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* === Campos comuns (mesma ordem do Clinica Geral) === */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2"><Label>Data</Label><Input type="date" value={pronForm.data_inicio || ''} onChange={e => setPronForm({...pronForm, data_inicio: e.target.value})} /></div>
          <div className="space-y-2"><Label>Idade</Label><Input value={pronForm.idade || ''} onChange={e => setPronForm({...pronForm, idade: e.target.value})} placeholder="Ex: 45 anos" /></div>
          <div className="space-y-2"><Label>Atividade Fisica</Label><Input value={pronForm.atividade_fisica || ''} onChange={e => setPronForm({...pronForm, atividade_fisica: e.target.value})} /></div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2"><Label>AP (Antecedentes Pessoais)</Label><textarea className="w-full border border-[#E5DCC8] rounded-md p-2 text-sm min-h-[70px] focus:outline-none focus:ring-1 focus:ring-[#0F1B3D]" value={pronForm.ap || ''} onChange={e => setPronForm({...pronForm, ap: e.target.value})} /></div>
          <div className="space-y-2"><Label>MUC (Medicacoes em Uso)</Label><textarea className="w-full border border-[#E5DCC8] rounded-md p-2 text-sm min-h-[70px] focus:outline-none focus:ring-1 focus:ring-[#0F1B3D]" value={pronForm.muc || ''} onChange={e => setPronForm({...pronForm, muc: e.target.value})} /></div>
        </div>
        <div className="space-y-2"><Label>Alergias</Label><Input value={pronForm.alergias || ''} onChange={e => setPronForm({...pronForm, alergias: e.target.value})} /></div>
        <div className="space-y-2"><Label>Queixa Principal</Label><textarea className="w-full border border-[#E5DCC8] rounded-md p-2 text-sm min-h-[70px] focus:outline-none focus:ring-1 focus:ring-[#0F1B3D]" value={pronForm.queixa_principal || ''} onChange={e => setPronForm({...pronForm, queixa_principal: e.target.value})} /></div>
        <div className="space-y-2"><Label>Conduta Final</Label><textarea className="w-full border border-[#E5DCC8] rounded-md p-2 text-sm min-h-[70px] focus:outline-none focus:ring-1 focus:ring-[#0F1B3D]" value={pronForm.conduta_final || ''} onChange={e => setPronForm({...pronForm, conduta_final: e.target.value})} /></div>

        <hr className="border-[#E5DCC8]" />
        <ExamesTextarea pronForm={pronForm} setPronForm={setPronForm} />

        {/* === Campos especificos do tratamento Tirzepatida === */}
        <hr className="border-[#E5DCC8]" />
        <h3 className="text-lg font-semibold text-[#0F1B3D]">Tratamento Tirzepatida</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2"><Label>Alimentacao</Label><Input value={pronForm.alimentacao || ''} onChange={e => setPronForm({...pronForm, alimentacao: e.target.value})} /></div>
          <div className="space-y-2"><Label>Med. emagrecimento anteriores</Label><Input value={pronForm.medicacoes_anteriores || ''} onChange={e => setPronForm({...pronForm, medicacoes_anteriores: e.target.value})} /></div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="space-y-2"><Label>Peso Inicial (kg)</Label><Input type="number" step="0.1" value={pronForm.peso_inicial || ''} onChange={e => setPronForm({...pronForm, peso_inicial: e.target.value})} /></div>
          <div className="space-y-2"><Label>Altura (cm)</Label><Input type="number" step="0.1" value={pronForm.altura || ''} onChange={e => setPronForm({...pronForm, altura: e.target.value})} /></div>
          <div className="space-y-2"><Label>IMC</Label><Input value={imcInicial} disabled className="bg-[#FAF6EE]" /></div>
          <div className="space-y-2"><Label>Peso Ideal (kg)</Label><Input type="number" step="0.1" value={pronForm.peso_ideal || ''} onChange={e => setPronForm({...pronForm, peso_ideal: e.target.value})} /></div>
          <div className="space-y-2"><Label>Peso Combinado</Label><Input type="number" step="0.1" value={pronForm.peso_combinado || ''} onChange={e => setPronForm({...pronForm, peso_combinado: e.target.value})} /></div>
        </div>
        <div className="space-y-2"><Label>Medicacao / dose</Label><Input value={pronForm.medicacao_dose || ''} onChange={e => setPronForm({...pronForm, medicacao_dose: e.target.value})} placeholder="Tirzepatida 2.5mg" /></div>

        <hr className="border-[#E5DCC8]" />
        <h3 className="text-lg font-semibold text-[#0F1B3D]">Cronograma de Uso</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
          <div className="space-y-2"><Label>Perda Total Esperada (kg)</Label><Input type="number" step="0.1" value={pronForm.perda_total_esperada || ''} onChange={e => setPronForm({...pronForm, perda_total_esperada: e.target.value})} /></div>
          <div className="space-y-2"><Label>Tempo Total</Label><Input value={pronForm.tempo_total || ''} onChange={e => setPronForm({...pronForm, tempo_total: e.target.value})} placeholder="Ex: 6 meses" /></div>
          <div className="space-y-2"><Label>Meta Combinada (kg)</Label><Input type="number" step="0.1" value={pronForm.meta_combinada || ''} onChange={e => setPronForm({...pronForm, meta_combinada: e.target.value})} /></div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm border border-[#E5DCC8] rounded-lg">
            <thead className="bg-[#FAF6EE]"><tr><th className="p-2 text-left text-[#0F1B3D]">Fase</th><th className="p-2">Semanas</th><th className="p-2">Dose</th><th className="p-2">Perda/sem</th><th className="p-2">Perda total</th><th className="p-2">Peso estimado</th></tr></thead>
            <tbody>{cronUso.map((r, i) => (
              <tr key={`cron-${i}`} className="border-t border-[#E5DCC8]">
                <td className="p-2 font-medium text-[#0F1B3D]">{r.fase}</td>
                <td className="p-1"><Input className="h-8 text-center" value={r.semanas || ''} onChange={e => updateCronUso(i, 'semanas', e.target.value)} /></td>
                <td className="p-1"><Input className="h-8 text-center" value={r.dose || ''} onChange={e => updateCronUso(i, 'dose', e.target.value)} /></td>
                <td className="p-1"><Input className="h-8 text-center" value={r.perda_esperada || ''} onChange={e => updateCronUso(i, 'perda_esperada', e.target.value)} /></td>
                <td className="p-1"><Input className="h-8 text-center" value={r.perda_total || ''} onChange={e => updateCronUso(i, 'perda_total', e.target.value)} /></td>
                <td className="p-1"><Input className="h-8 text-center" value={r.peso_estimado || ''} onChange={e => updateCronUso(i, 'peso_estimado', e.target.value)} /></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
        <h3 className="text-lg font-semibold text-[#0F1B3D]">Cronograma de Desmame</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm border border-[#E5DCC8] rounded-lg">
            <thead className="bg-[#FAF6EE]"><tr><th className="p-2 text-left text-[#0F1B3D]">Fase</th><th className="p-2">Semanas</th><th className="p-2">Dose</th></tr></thead>
            <tbody>{cronDesmame.map((r, i) => (
              <tr key={`desm-${i}`} className="border-t border-[#E5DCC8]">
                <td className="p-2 font-medium text-[#0F1B3D]">{r.fase}</td>
                <td className="p-1"><Input className="h-8 text-center" value={r.semanas || ''} onChange={e => updateCronDesmame(i, 'semanas', e.target.value)} /></td>
                <td className="p-1"><Input className="h-8 text-center" value={r.dose || ''} onChange={e => updateCronDesmame(i, 'dose', e.target.value)} /></td>
              </tr>
            ))}</tbody>
          </table>
        </div>

        <hr className="border-[#E5DCC8]" />
        <NextReturnSection pronForm={pronForm} setPronForm={setPronForm} pronData_inicio={pronForm.data_inicio} />
      </CardContent>
    </Card>
  );
};
